import { useState, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { weeklyDataService } from '@/services/weeklyDataService';
import type { Player } from '@/types/player';

interface WeeklyBreakdownRequest {
  roster: Player[];
  league: {
    teamCount: number;
    roster: {
      QB: number;
      RB: number;
      WR: number;
      TE: number;
      K: number;
      DEF: number;
      FLEX: number;
      SUPERFLEX?: number;
    };
    scoring: string;
  };
  week: number;
}

interface WeeklyRosterBreakdownProps {
  roster: Player[];
  leagueSettings: WeeklyBreakdownRequest['league'];
  className?: string;
  onDismiss?: () => void;
  forceRefresh?: number;
}

export const WeeklyRosterBreakdown: React.FC<WeeklyRosterBreakdownProps> = ({
  roster,
  leagueSettings,
  className = ''
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [breakdown, setBreakdown] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [currentWeek, setCurrentWeek] = useState(getCurrentWeek());
  const [lastGeneratedDate, setLastGeneratedDate] = useState<string | null>(null);
  const [isCollapsed, setIsCollapsed] = useState(false);

  // Auto-load breakdown when component mounts with valid roster
  useEffect(() => {
    if (roster.length >= 3 && !hasLoaded && !isLoading) {
      // Check for cached data first
      const cached = getCachedBreakdown();
      if (cached) {
        console.log('Loading cached weekly breakdown');
        setBreakdown(cached.content);
        setLastGeneratedDate(cached.generatedDate);
        setHasLoaded(true);
      } else {
        // Only generate new breakdown if no cache exists
        handleLoadBreakdown();
      }
    }
  }, [roster.length, hasLoaded, isLoading]);

  const handleLoadBreakdown = useCallback(async () => {
    if (roster.length < 3) {
      setError('Add at least 3 players to see weekly breakdown');
      return;
    }

    // Prevent regeneration if we already have today's breakdown
    const cached = getCachedBreakdown();
    if (cached) {
      console.log('Breakdown already generated today, using cached version');
      return;
    }

    setIsLoading(true);
    setBreakdown('');
    setError(null);
    setHasLoaded(true);

    const request: WeeklyBreakdownRequest = {
      roster: roster.slice(0, 12), // Limit to starting lineup + key bench
      league: leagueSettings,
      week: currentWeek
    };

    try {
      await fetchWeeklyParagraph(request);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load weekly breakdown');
    } finally {
      setIsLoading(false);
    }
  }, [roster, leagueSettings]);

  // Helper function to get current NFL week
  function getCurrentWeek(): number {
    return weeklyDataService.getCurrentWeek();
  }

  // Helper functions for daily caching
  const getCacheKey = () => `weekly-breakdown-${currentWeek}-${roster.map(p => p.id).sort().join('-')}`;
  const getTodayString = () => new Date().toISOString().split('T')[0]; // YYYY-MM-DD
  
  const getCachedBreakdown = () => {
    try {
      const cacheKey = getCacheKey();
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        const data = JSON.parse(cached);
        // Check if cache is from today
        if (data.generatedDate === getTodayString()) {
          return data;
        }
      }
    } catch (err) {
      console.warn('Failed to read cached breakdown:', err);
    }
    return null;
  };

  const setCachedBreakdown = (content: string) => {
    try {
      const cacheKey = getCacheKey();
      const data = {
        content,
        generatedDate: getTodayString(),
        week: currentWeek,
        roster: roster.map(p => ({ id: p.id, name: p.name }))
      };
      localStorage.setItem(cacheKey, JSON.stringify(data));
    } catch (err) {
      console.warn('Failed to cache breakdown:', err);
    }
  };

  // Fetch weekly paragraph from API
  async function fetchWeeklyParagraph(payload: WeeklyBreakdownRequest): Promise<void> {
    try {
      // Use relative URL in production, full URL in development
      const apiBase = import.meta.env.VITE_API_BASE || '';
      const apiUrl = apiBase ? `${apiBase}/api/roster/weekly-breakdown` : '/api/roster/weekly-breakdown';
      
      console.log(`Making request to: ${apiUrl}`);
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      console.log(`Response status: ${response.status} ${response.statusText}`);

      if (!response.ok) {
        let errorMessage = `Weekly breakdown failed: ${response.status}`;
        
        const responseText = await response.text();
        let errorData: any;
        
        try {
          errorData = responseText ? JSON.parse(responseText) : {};
        } catch {
          errorData = { error: responseText || 'Unknown error' };
        }
        
        console.error(`API Error Response:`, errorData);
        
        if (errorData.message) {
          errorMessage = `${errorMessage} - ${errorData.message}`;
        }
        
        if (errorData.requestId) {
          errorMessage = `${errorMessage} (Request ID: ${errorData.requestId})`;
        }
        
        // Provide user-friendly descriptions for common errors
        if (response.status === 500 && errorData.message?.includes('FUNCTION_INVOCATION_FAILED')) {
          errorMessage = 'AI service temporarily unavailable. Our team is working to fix this. Please try again in a few minutes.';
        } else if (response.status === 400) {
          errorMessage = 'Invalid roster data. Please check your lineup and try again.';
        } else if (response.status === 502) {
          errorMessage = 'AI model temporarily unavailable. Please try again shortly.';
        } else if (!errorData.message && responseText) {
          errorMessage = `${errorMessage} - ${responseText}`;
        }
        
        throw new Error(errorMessage);
      }

      const responseText = await response.text();
      let data: any;
      
      try {
        data = responseText ? JSON.parse(responseText) : {};
      } catch {
        throw new Error('Invalid JSON response from server');
      }
      
      console.log('Received JSON response:', data);
      
      if (data.paragraph) {
        setBreakdown(data.paragraph);
        setCachedBreakdown(data.paragraph);
        setLastGeneratedDate(getTodayString());
      } else {
        throw new Error(data.error || 'Invalid response format');
      }
    } catch (error) {
      // Fallback to mock data for development
      if (import.meta.env.DEV) {
        console.warn('Using mock weekly breakdown data');
        const mockParagraph = `Quarterbacks
Jalen Hurts brings elite rushing upside against a defense that just gave up 60 yards on the ground to mobile QBs, while Drake Maye profiles as a volatile but high-ceiling QB2 if game flow leans pass-heavy. Together, you've got a safe floor with true shootout potential.

Running Backs
The Etienne brothers headline a crowded but explosive backfield; Trevor has a favorable matchup on the edges, while Travis faces a tougher run defense but remains a red-zone staple. DJ Giddens and Devin Neal add depth but may be game-script dependent.

Wide Receivers
Deebo Samuel and Terry McLaurin provide steady target shares, while Mike Evans projects for high-volume red-zone looks against a leaky secondary. Jimmy Horn remains more of a dart throw, his ceiling tied to broken plays or soft coverage.

Tight End / Kicker
T.J. Hockenson offers a safe 6 to 8 targets with top-5 weekly TE potential, while Matt Gay should benefit from multiple red-zone trips in a matchup with projected high scoring.

Weekly Outlook
This lineup has top-end QB/RB upside and reliable TE production, but overall ceiling hinges on WR consistency and whether the backfield avoids cannibalizing touches.`;
        
        setBreakdown(mockParagraph);
        setCachedBreakdown(mockParagraph);
        setLastGeneratedDate(getTodayString());
      } else {
        throw error;
      }
    }
  }

  if (roster.length === 0) {
    return null; // Don't show anything if no roster
  }

  return (
    <div className={`max-w-4xl mx-auto mb-8 ${className}`}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gray-900/50 border border-gray-700 rounded-xl overflow-hidden backdrop-blur-sm"
      >
        {/* Header */}
        <div className="px-4 md:px-6 py-4 border-b border-gray-700 flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <h2 className="text-lg font-semibold text-fiesta-yellow mb-1">Week {currentWeek} Breakdown</h2>
            <p className="text-xs text-gray-400 hidden sm:block">
              AI-powered weekly outlook
              {lastGeneratedDate && (
                <span className="ml-2 text-gray-500">
                  • Generated {lastGeneratedDate === getTodayString() ? 'today' : lastGeneratedDate}
                </span>
              )}
            </p>
          </div>
          
          <div className="flex items-center gap-3 flex-shrink-0">
            {/* Collapse/Expand button */}
            <button
              onClick={() => setIsCollapsed(!isCollapsed)}
              className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors flex-shrink-0"
              title={isCollapsed ? "Expand weekly breakdown" : "Collapse weekly breakdown"}
            >
              <svg className={`w-4 h-4 transition-transform ${isCollapsed ? '' : 'rotate-180'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content - collapsible */}
        <AnimatePresence>
          {!isCollapsed && (
            <motion.div
              initial={{ height: 'auto', opacity: 1 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="p-6"
            >
              {/* Loading State */}
              {isLoading && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-center py-8"
                >
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-fiesta-blue mx-auto mb-4"></div>
                  <span className="text-gray-400">Analyzing week {currentWeek} matchups...</span>
                </motion.div>
              )}

              {/* Error State */}
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="p-4 bg-red-900/20 border border-red-800 rounded-lg"
                >
                  <div className="flex items-center gap-2 text-red-400">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm">{error}</span>
                  </div>
                </motion.div>
              )}

              {/* Breakdown Content */}
              {breakdown && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-gray-200 leading-relaxed text-base space-y-3"
                >
                  {breakdown.split('\n\n').map((section, index) => {
                    const lines = section.split('\n').filter(line => line.trim());
                    if (lines.length === 0) return null;
                    
                    const [title, ...content] = lines;
                    const isSection = ['Quarterbacks', 'Running Backs', 'Wide Receivers', 'Tight End / Kicker', 'Weekly Outlook'].includes(title.trim());
                    
                    if (isSection) {
                      return (
                        <div key={index} className="space-y-1">
                          <h4 className="text-fiesta-yellow font-medium text-sm">{title}</h4>
                          <p className="text-gray-200 text-sm leading-relaxed">
                            {content.join(' ').trim()}
                          </p>
                        </div>
                      );
                    } else {
                      // Fallback for single paragraph format
                      return (
                        <p key={index} className="text-gray-200">
                          {section}
                        </p>
                      );
                    }
                  })}
                </motion.div>
              )}

              {/* Empty State for insufficient roster */}
              {roster.length > 0 && roster.length < 3 && !isLoading && (
                <div className="text-center py-6">
                  <div className="text-gray-400">
                    <svg className="w-8 h-8 mx-auto mb-2 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    <p className="text-sm">Add more players to see weekly breakdown</p>
                    <p className="text-xs text-gray-500 mt-1">Need at least 3 players for analysis</p>
                  </div>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
};